<template>
  <div class="country-search">
    <label v-if="label" class="lbl">{{ label }}</label>
    <div class="wrap" :class="{ open }">
      <input
        ref="inputEl"
        type="text"
        class="inp"
        :placeholder="placeholder"
        autocomplete="off"
        autocapitalize="off"
        spellcheck="false"
        :value="open ? queryText : displayLabel"
        @focus="onFocus"
        @input="onInput"
        @keydown.escape.prevent="close"
      />
      <button
        v-if="modelValue"
        type="button"
        class="clear"
        aria-label="Limpar país"
        tabindex="-1"
        @mousedown.prevent="clear"
      >
        ×
      </button>
      <ul v-show="open && filtered.length" class="list" role="listbox">
        <li
          v-for="opt in filtered"
          :key="opt.code"
          role="option"
          class="item"
          @mousedown.prevent="pick(opt)"
        >
          <span class="name">{{ opt.name }}</span>
          <span class="code">{{ opt.code }}</span>
        </li>
      </ul>
      <p v-show="open && queryText.trim() && !filtered.length" class="empty">Nenhum país encontrado</p>
    </div>
  </div>
</template>

<script>
import { getSortedCountryOptions } from "../utils/countriesPt";
import { foldAccent } from "../utils/searchNormalize";

const MAX_LIST = 80;

export default {
  name: "CountrySearchField",
  props: {
    modelValue: {
      type: String,
      default: "",
    },
    label: {
      type: String,
      default: "País",
    },
    placeholder: {
      type: String,
      default: "Buscar país (nome ou código)",
    },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      open: false,
      queryText: "",
      options: getSortedCountryOptions(),
    };
  },
  computed: {
    displayLabel() {
      const v = String(this.modelValue || "").trim().toUpperCase();
      if (!v) return "";
      const o = this.options.find((x) => x.code === v);
      return o ? o.name : v;
    },
    filtered() {
      const q = foldAccent(this.queryText.trim());
      if (!q) {
        return this.options.slice(0, MAX_LIST);
      }
      return this.options
        .filter(
          (o) =>
            foldAccent(o.name).includes(q) ||
            foldAccent(o.code).includes(q)
        )
        .slice(0, MAX_LIST);
    },
  },
  watch: {
    modelValue() {
      if (!this.open) this.queryText = "";
    },
  },
  mounted() {
    this._docDown = (e) => {
      if (this.$el && !this.$el.contains(e.target)) this.close();
    };
    document.addEventListener("mousedown", this._docDown);
  },
  beforeUnmount() {
    document.removeEventListener("mousedown", this._docDown);
  },
  methods: {
    onFocus() {
      this.open = true;
      this.queryText = this.displayLabel || "";
    },
    onInput(e) {
      this.queryText = e.target.value;
      this.open = true;
    },
    pick(opt) {
      const code = String(opt.code || "").toUpperCase();
      this.$emit("update:modelValue", code);
      this.queryText = opt.name || "";
      this.open = false;
      this.$refs.inputEl?.blur();
    },
    clear() {
      this.$emit("update:modelValue", "");
      this.queryText = "";
      this.open = false;
    },
    close() {
      this.open = false;
      this.queryText = "";
    },
  },
};
</script>

<style scoped>
.country-search {
  display: flex;
  flex-direction: column;
  gap: 6px;
  position: relative;
  min-width: 0;
}
.lbl {
  color: #94adc8;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.wrap {
  position: relative;
}
.inp {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #1e3352;
  border-radius: 9px;
  padding: 10px 32px 10px 10px;
  color: #e2eaf4;
  font-size: 14px;
  background: #081526;
}
.wrap.open .inp {
  border-color: #4f8ef7;
  box-shadow: 0 0 0 2px rgba(79, 142, 247, 0.15);
}
.clear {
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: #1a2e48;
  color: #94adc8;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.clear:hover {
  background: #1e3352;
  color: #e2eaf4;
}
.list {
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 4px);
  z-index: 40;
  margin: 0;
  padding: 4px 0;
  list-style: none;
  max-height: 240px;
  overflow-y: auto;
  background: #152338;
  border: 1px solid #1e3352;
  border-radius: 10px;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.4);
}
.item {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
  color: #e2eaf4;
}
.item:hover {
  background: #1e3a8a;
}
.name {
  font-weight: 600;
}
.code {
  color: #94adc8;
  font-size: 12px;
  font-weight: 700;
}
.empty {
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 4px);
  z-index: 40;
  margin: 0;
  padding: 12px;
  font-size: 13px;
  color: #94adc8;
  background: #152338;
  border: 1px solid #1e3352;
  border-radius: 10px;
}
</style>
